import React, { useState, useEffect } from 'react';
import { Form, Select, Input, Button, DatePicker, message } from 'antd';
import dayjs from 'dayjs';

const { Option } = Select;

// Offboarding Form Component
const OffboardingForm = ({ form, onSubmit, initialValues, isEdit, employees, onStatusUpdate }) => {
    const [loading, setLoading] = useState(false);
    const [comments, setComments] = useState('');
  
    // Set initial values when component mounts or initialValues change
    useEffect(() => {
      if (isEdit && initialValues) {
        setComments(initialValues.comments || '');
      }
    }, [isEdit, initialValues]);
  
    const handleSubmit = async (values) => {
      setLoading(true);
      try {
        // Preserve API shape by always sending an array for employeeIds
        const payload = {
          ...values,
          employeeIds: values.employeeIds ? [values.employeeIds] : [],
        };
        await onSubmit(payload);
      } finally {
        setLoading(false);
      }
    };
  
    return (
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={isEdit && initialValues ? {
          employeeIds: initialValues.employeeId && Array.isArray(initialValues.employeeId)
            ? (initialValues.employeeId[0]?._id || undefined)
            : (initialValues.employeeId?._id || undefined),
          status: initialValues.status,
          resignDate: initialValues.resignationDate ? dayjs(initialValues.resignationDate) : null,
          lastWorkingDate: initialValues.lastWorkingDate ? dayjs(initialValues.lastWorkingDate) : null,
          noOfDays: initialValues.noticePeriod,
          reason: initialValues.reason,
          comments: initialValues.comments || ''
        } : {}}
      >
        <div className="flex flex-wrap gap-4">
          <Form.Item
            className='flex-1 min-w-[200px]'
            name="employeeIds"
            label="Employee"
            rules={[{ required: true, message: 'Please select an employee' }]}
          >
            <Select
              placeholder="Select Employee"
              showSearch
              optionFilterProp="children"
              style={{ width: '100%' }}
              allowClear
            >
              {employees.map(employee => (
                <Option key={employee._id} value={employee._id}>
                  {employee.name} ({employee.department})
                </Option>
              ))}
            </Select>
          </Form.Item>
  
          <Form.Item
            className='flex-1 min-w-[200px]'
            name="status"
            label="Status"
            rules={[{ required: true, message: 'Please select status' }]}
          >
            <Select placeholder="Select Status">
              <Option value="Resigned">Resigned</Option>
              <Option value="Termination">Termination</Option>
              <Option value="LayOff">LayOff</Option>
            </Select>
          </Form.Item>
        </div>
  
        <div className="flex flex-wrap gap-4">
          <Form.Item
            className='flex-1 min-w-[200px]'
            name="resignDate"
            label="Resignation Date"
            rules={[{ required: true, message: 'Please select resignation date' }]}
          >
            <DatePicker className="w-full" />
          </Form.Item>
  
          <Form.Item
            className='flex-1 min-w-[200px]'
            name="lastWorkingDate"
            label="Last Working Date"
            rules={[{ required: true, message: 'Please select last working date' }]}
          >
            <DatePicker className="w-full" />
          </Form.Item>
  
          <Form.Item
            className='flex-1 min-w-[200px]'
            name="noOfDays"
            label="Notice Period"
            rules={[{ required: true, message: 'Please enter Notice Period' }]}
          >
            <Input type="number" className="w-full" placeholder="Enter Notice Period" />
          </Form.Item>
        </div>
  
        <Form.Item
          name="reason"
          label="Reason for Offboarding"
          rules={[{ required: true, message: 'Please provide reason for offboarding' }]}
        >
          <Input.TextArea
            rows={4}
            placeholder="Please provide detailed reason for offboarding"
          />
        </Form.Item>
  
        {isEdit && initialValues?.statusResign === "pending" && (
          <Form.Item
            name="comments"
            label="Comments"
            rules={[{ required: true, message: 'Please provide comments for this action' }]}
          >
            <Input.TextArea
              rows={2}
              placeholder="Please provide comments for accepting or rejecting this request"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
            />
          </Form.Item>
        )}
  
        <div className="flex justify-end gap-4 mt-6">
          <Button
            type="default"
            onClick={() => form.resetFields()}
          >
            Reset
          </Button>
          {isEdit && initialValues?.statusResign === "pending" && (
            <>
              <Button
                type="primary"
                danger
                onClick={() => {
                  if (!comments.trim()) {
                    message.error('Please provide comments before rejecting the request');
                    return;
                  }
                  onStatusUpdate("reject", comments);
                }}
                loading={loading}
              >
                Reject Request
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  if (!comments.trim()) {
                    message.error('Please provide comments before accepting the request');
                    return;
                  }
                  onStatusUpdate("accept", comments);
                }}
                loading={loading}
                className="bg-primary text-white"
              >
                Accept Request
              </Button>
            </>
          )}
          {!isEdit && (
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              className="bg-primary text-white"
            >
              Submit Request
            </Button>
          )}
        </div>
      </Form>
    );
  };

export default OffboardingForm;